/**
 * PlatformUsageCard - Card de estatísticas de uso por plataforma (PWA vs Browser)
 * 
 * Exibe distribuição de acessos nos últimos 30 dias entre:
 * - Browser (navegador web)
 * - PWA (app instalado Android/Desktop)
 * - PWA iOS (app instalado no iPhone/iPad)
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Smartphone, Globe, Monitor } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Interface para dados de plataforma */
interface PlatformData {
  platform: string;
  count: number;
  icon: React.ElementType;
  label: string;
}

/** Mapeamento de ícones e labels por plataforma */
const PLATFORM_CONFIG: Record<string, { icon: React.ElementType; label: string }> = {
  browser: { icon: Globe, label: 'Browser' },
  pwa: { icon: Monitor, label: 'PWA (Android/Desktop)' },
  'pwa-ios': { icon: Smartphone, label: 'PWA (iOS)' },
};

export function PlatformUsageCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-platform-usage'],
    queryFn: async () => {
      // Buscar contagem agrupada por plataforma (últimos 30 dias)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('last_access_platform')
        .gte('last_access_at', thirtyDaysAgo.toISOString())
        .not('last_access_platform', 'is', null);

      if (error) throw error;

      // Agrupar manualmente no frontend (evita RPC adicional)
      const counts = new Map<string, number>();
      (profiles || []).forEach((p: { last_access_platform: string | null }) => {
        const platform = p.last_access_platform || 'browser';
        counts.set(platform, (counts.get(platform) || 0) + 1);
      });

      const platforms: PlatformData[] = Array.from(counts.entries()).map(([platform, count]) => ({
        platform,
        count,
        icon: PLATFORM_CONFIG[platform]?.icon || Globe,
        label: PLATFORM_CONFIG[platform]?.label || platform,
      }));

      // Ordenar por contagem decrescente
      platforms.sort((a, b) => b.count - a.count);

      const total = platforms.reduce((sum, p) => sum + p.count, 0);

      return { platforms, total };
    },
    staleTime: 1000 * 60 * 5, // 5 minutos de cache
  });

  const totalUsers = data?.total || 0;
  const pwaUsers = (data?.platforms || [])
    .filter(p => p.platform === 'pwa' || p.platform === 'pwa-ios')
    .reduce((sum, p) => sum + p.count, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-muted-foreground" />
          Uso por Plataforma (30 dias)
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        ) : totalUsers === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum dado de plataforma registrado ainda.
          </p>
        ) : (
          <div className="space-y-3">
            {/* Resumo rápido */}
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-foreground">{pwaUsers}</span>
              <span className="text-sm text-muted-foreground">
                de {totalUsers} usuários via PWA
                {totalUsers > 0 && ` (${Math.round((pwaUsers / totalUsers) * 100)}%)`}
              </span>
            </div>

            {/* Barras de distribuição */}
            <div className="space-y-2">
              {(data?.platforms || []).map((p) => {
                const Icon = p.icon;
                const percentage = totalUsers > 0 ? Math.round((p.count / totalUsers) * 100) : 0;
                return (
                  <div key={p.platform} className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-foreground font-medium">{p.label}</span>
                        <span className="text-muted-foreground">{p.count} ({percentage}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
